import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Platform, StyleSheet, View } from "react-native";
import {
  getAllItemsEverCraftable,
  getItemByIdNullable,
} from "../../backend/items/Items";
import { PlayerContext } from "../../backend/PlayerContext";
import {
  addContextListener,
  clearContextListener,
} from "../../backend/PlayerContextListeners";
import { CheckBox } from "../utility/CheckBox";
import { Picker as UtilityPicker } from "../utility/Picker";
import { UIContext, useUIStateFromContext } from "../utility/UIContext";
import { CraftingDetails } from "./CraftingDetails";
import { ItemGrid } from "./ItemGrid";

const TAG_ORDER = [
  "Quest Item",
  "Consumable",
  "Hand",
  "Body",
  "Accessory",
  "Equipment",
  "Material",
];

export default function Crafting() {
  const playerContext = useContext(PlayerContext);
  const uiContext = useContext(UIContext);
  const [selectedIndex, setSelectedIndex] = useState(null as number | null);
  const [selectedTag, setSelectedTag] = useUIStateFromContext(
    uiContext,
    "CraftingTag",
    "All",
  );
  const maxSchoolLevels = playerContext.global.maxSchoolLevels;
  let craftableItems = useMemo(() => {
    const allItems = getAllItemsEverCraftable(playerContext);
    return allItems;
  }, [JSON.stringify(maxSchoolLevels)]);

  const tagSet = new Set<string>();
  craftableItems.forEach((item) => {
    const tags = item.getTags(playerContext, item.getDefaultParams());
    tags.forEach((tag) => tagSet.add(tag.tag));
  });
  const tags = Array.from(tagSet).sort((a, b) => {
    const indexA = TAG_ORDER.findIndex((tag) => tag == a);
    const indexB = TAG_ORDER.findIndex((tag) => tag == b);
    return indexA - indexB;
  });

  useEffect(() => {
    if (!tags.includes(selectedTag)) {
      setSelectedTag("All");
    }
  }, [tags.join(), selectedTag]);

  useEffect(() => setSelectedIndex(null), [selectedTag]);

  const selectedIndexRef = useRef(selectedIndex);
  selectedIndexRef.current = selectedIndex;

  useEffect(() => {
    addContextListener(
      "CraftingComponent",
      (newState) => {
        setSelectedIndex(null);
      },
      (state) =>
        getAllItemsEverCraftable(state)
          .map((item) => item.getId())
          .join(),
    );
    return () => clearContextListener("CraftingComponent");
  }, []);

  const onPressItem = React.useCallback(
    (item: any, index: number) => {
      setSelectedIndex(index);
    },
    [setSelectedIndex],
  );

  const tagButtons = useMemo(() => {
    return (
      <UtilityPicker
        onValueChange={setSelectedTag}
        style={styles.picker}
        selectedValue={selectedTag}
        data={["All", ...tags].map((tag) => ({
          label: tag,
          value: tag,
          key: tag,
        }))}
      />
    );
  }, [setSelectedTag, selectedTag, JSON.stringify(tags)]);

  craftableItems = craftableItems
    .filter(
      (item) =>
        selectedTag === "All" ||
        item
          .getTags(playerContext, item.getDefaultParams())
          .find((tag) => tag.tag == selectedTag),
    )
    .sort((a, b) => {
      const firstTagA = tags.findIndex((tag) =>
        a
          .getTags(playerContext, a.getDefaultParams())
          .find((innerTag) => innerTag.tag == tag),
      );
      const firstTagB = tags.findIndex((tag) =>
        b
          .getTags(playerContext, b.getDefaultParams())
          .find((innerTag) => innerTag.tag == tag),
      );
      if (firstTagA != firstTagB) {
        return firstTagA - firstTagB;
      }
      const maxA = Math.max(
        ...(Object.values(
          a.getCraftingSchoolLevelRequirements(playerContext),
        ).filter((n) => n != undefined) as number[]),
      );
      const maxB = Math.max(
        ...(Object.values(
          b.getCraftingSchoolLevelRequirements(playerContext),
        ).filter((n) => n != undefined) as number[]),
      );
      return maxA - maxB;
    });

  const [hideLocked, setHideLocked] = useUIStateFromContext(
    uiContext,
    "crafting_hide_locked",
    false,
  );

  const toggleLockedCallback = useCallback(() => {
    setHideLocked(!hideLocked);
  }, [hideLocked, setHideLocked]);

  useEffect(() => setSelectedIndex(null), [hideLocked]);

  const itemGridElements = craftableItems.map((item) => ({
    itemId: item.getId(),
    amount: 1,
    params: item.getDefaultParams(),
    disabled: !item.isCraftable(playerContext),
  }));

  const itemGridElementsJSON = JSON.stringify(itemGridElements);

  const isAnyLocked = useMemo(() => {
    return itemGridElements.some((element) => element.disabled);
  }, [itemGridElementsJSON]);

  const itemGridElementsFiltered = useMemo(() => {
    if (hideLocked) {
      return itemGridElements
        .filter((itemGridElement) => !itemGridElement.disabled)
        .map((element, index) => ({
          ...element,
          disableTooltip: index === selectedIndex,
        }));
    }
    return itemGridElements.map((element, index) => ({
      ...element,
      disableTooltip: index === selectedIndex,
    }));
  }, [itemGridElementsJSON, hideLocked, selectedIndex]);

  const itemGridElementsFilteredJSON = JSON.stringify(itemGridElementsFiltered);

  const selectedItem =
    selectedIndex != null
      ? getItemByIdNullable(itemGridElementsFiltered[selectedIndex]?.itemId)
      : undefined;

  const itemGrid = useMemo(
    () => (
      <ItemGrid
        items={itemGridElementsFiltered}
        crafting={true}
        onPressItem={onPressItem}
      />
    ),
    [itemGridElementsFilteredJSON, onPressItem],
  );

  const details = useMemo(
    () => (
      <View style={styles.detailsContainer}>
        {selectedItem && <CraftingDetails item={selectedItem} />}
      </View>
    ),
    [selectedItem?.getId()],
  );

  return (
    <View style={styles.container}>
      <View style={styles.row}>
        <View style={styles.tagPickerContainer}>{tagButtons}</View>
        {isAnyLocked && (
          <CheckBox
            title="Hide locked"
            checked={hideLocked}
            onPress={toggleLockedCallback}
            textStyle={styles.checkboxText}
            containerStyle={styles.checkboxContainer}
          />
        )}
      </View>
      {itemGrid}
      {details}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "stretch",
    justifyContent: "flex-start",
    width: "100%",
  },
  detailsContainer: {
    marginTop: 16,
    paddingHorizontal: 8,
    paddingBottom: 8,
  },
  buttonGroupContainer: {
    flexDirection: "row",
    alignItems: "flex-start",
    height: undefined,
    flexWrap: "wrap",
    justifyContent: "flex-start",
    borderWidth: 0,
  },
  buttonGroupButtonContainer: {
    flex: 1,
    height: 40,
    borderWidth: 1,
    borderColor: "#eee",
    margin: 4,
  },
  picker: {
    flex: 1,
  },
  tagPickerContainer: {
    width: 180,
    marginHorizontal: 8,
    ...Platform.select({
      android: { backgroundColor: "#eee", borderRadius: 10, default: {} },
    }),
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    marginBottom: 16,
  },
  checkboxContainer: {
    marginLeft: 0,
    padding: 0,
  },
  checkboxText: {
    fontWeight: "normal",
  },
});
